import { PortalPartnerRO } from '@escapenavigator/types/dist/partners-portal/portal-partner.ro';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { api } from '../api';

type AuthContextValue = {
    partner: PortalPartnerRO | null;
    initializing: boolean;
    setPartner: (partner: PortalPartnerRO | null) => void;
    refresh: () => Promise<void>;
    logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [partner, setPartner] = useState<PortalPartnerRO | null>(null);
    const [initializing, setInitializing] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.me();

            setPartner(data);
        } catch {
            setPartner(null);
        }
    }, []);

    useEffect(() => {
        refresh().finally(() => setInitializing(false));
    }, [refresh]);

    const logout = useCallback(async () => {
        try {
            await api.logout();
        } finally {
            setPartner(null);
        }
    }, []);

    const value = useMemo(
        () => ({ partner, initializing, setPartner, refresh, logout }),
        [partner, initializing, refresh, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
